// src/components/TimePicker/index.js - UPDATED WITH PAST TIME FILTERING
import React, { useState, useEffect } from 'react';
import { Clock, Calendar, ChevronLeft, ChevronRight, AlertCircle, Info } from 'lucide-react';
import { generateTimeSlots, formatTimeDisplay } from '../../utils/timeSlotGenerator';
import { settingsService } from '../../config/settingsService';
import './index.css';

const TimePicker = ({
  weekSchedule,
  selectedDateTime,
  onSelectDateTime,
  className = '',
  isPreOrder = false,
  preSelectedDate = null,
  orderType = 'collection'
}) => {
  const [timeSlots, setTimeSlots] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(0);

  useEffect(() => {
    if (weekSchedule) {
      // ✅ 1 day for immediate orders, 14 days for pre-orders
      const daysToGenerate = isPreOrder ? 14 : 1;

      console.log('📅 TimePicker generating slots:', {
        isPreOrder,
        daysToGenerate,
        orderType
      });

      // Generate time slots - past slots are automatically filtered out
      const slotDuration = settingsService.getScheduleSlotDuration();
      const slots = generateTimeSlots(weekSchedule, daysToGenerate, slotDuration);
      setTimeSlots(slots);

      console.log('📅 Generated slots for dates:', Object.keys(slots));

      // Set initial selected date
      if (preSelectedDate && slots[preSelectedDate]) {
        console.log('📅 Using pre-selected date:', preSelectedDate);
        setSelectedDate(preSelectedDate);
      } else if (!selectedDate && Object.keys(slots).length > 0) {
        const firstDate = Object.keys(slots).sort()[0];
        setSelectedDate(firstDate);
      }
    }
  }, [weekSchedule, isPreOrder, preSelectedDate]);

  useEffect(() => {
    // Parse existing selectedDateTime if provided
    if (selectedDateTime) {
      const [date, time] = selectedDateTime.split(' ');
      setSelectedDate(date);
      // ✅ FIX: Normalize time to HH:MM format (remove seconds if present)
      const normalizedTime = time?.split(':').slice(0, 2).join(':') || '';
      setSelectedTime(normalizedTime);
    }
  }, [selectedDateTime]);

  const handleDateSelect = (dateKey) => {
    setSelectedDate(dateKey);
    setSelectedTime(null);

    // Clear selection when date changes
    if (onSelectDateTime) {
      onSelectDateTime(null);
    }
  };

  const handleTimeSelect = (timeStr) => {
    if (!selectedDate) return;

    // ✅ Normalize for comparison - remove seconds if present
    const normalizedNewTime = timeStr?.split(':').slice(0, 2).join(':') || '';
    const normalizedCurrentTime = selectedTime?.split(':').slice(0, 2).join(':') || '';

    // ✅ TOGGLE: If clicking same time, deselect it
    if (normalizedCurrentTime === normalizedNewTime && normalizedCurrentTime !== '') {
      setSelectedTime(null);
      if (onSelectDateTime) {
        onSelectDateTime(null);
      }
      return;
    }

    // Otherwise select the new time
    setSelectedTime(normalizedNewTime);

    const dateTime = `${selectedDate} ${normalizedNewTime}:00`;
    if (onSelectDateTime) {
      onSelectDateTime(dateTime);
    }
  };

  if (!weekSchedule || Object.keys(timeSlots).length === 0) {
    return (
      <div className="time-picker-empty">
        <AlertCircle size={24} />
        <p>No {isPreOrder ? 'scheduled' : 'collection'} times available</p>
      </div>
    );
  }

  const sortedDates = Object.keys(timeSlots).sort();
  const availableDates = sortedDates.slice(currentWeekStart, currentWeekStart + 7);
  const selectedDayData = selectedDate ? timeSlots[selectedDate] : null;

  // Get store hours for selected date
  const getStoreHours = () => {
    if (!selectedDayData || !selectedDayData.slots.length) return null;

    const availableSlots = selectedDayData.slots.filter(s => !s.disabled);
    if (availableSlots.length === 0) return null;

    const firstSlot = availableSlots[0];
    const lastSlot = availableSlots[availableSlots.length - 1];

    return {
      open: firstSlot.display,
      close: lastSlot.display
    };
  };

  const storeHours = getStoreHours();

  return (
    <div className={`time-picker-container ${className}`}>

      {/* Date Selector */}
      <div className="time-picker-section">
        <div className="time-picker-header">
          <Calendar size={18} />
          <span>Select Date</span>
        </div>

        <div className="date-selector">
          {currentWeekStart > 0 && (
            <button
              className="date-nav-btn prev"
              onClick={() => setCurrentWeekStart(Math.max(0, currentWeekStart - 7))}
              aria-label="Previous week"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          <div className="date-list">
            {availableDates.map((dateKey) => {
              const dayData = timeSlots[dateKey];
              const isSelected = selectedDate === dateKey;
              const hasSlots = dayData.slots.some(s => !s.disabled);

              return (
                <button
                  key={dateKey}
                  className={`date-card ${isSelected ? 'selected' : ''} ${!hasSlots ? 'disabled' : ''}`}
                  onClick={() => hasSlots && handleDateSelect(dateKey)}
                  disabled={!hasSlots}
                  aria-label={`${dayData.displayDate}`}
                >
                  <div className="date-day">{dayData.dayName.slice(0, 3)}</div>
                  <div className="date-number">{dayData.displayDate.split(' ')[1]}</div>
                  <div className="date-month">{dayData.displayDate.split(' ')[0]}</div>
                </button>
              );
            })}
          </div>

          {currentWeekStart + 7 < sortedDates.length && (
            <button
              className="date-nav-btn next"
              onClick={() => setCurrentWeekStart(currentWeekStart + 7)}
              aria-label="Next week"
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Store Hours Info - Only for Pre-Order */}
      {isPreOrder && selectedDate && storeHours && (
        <div className="store-hours-info">
          <Clock size={16} />
          <span>
            Store hours: {storeHours.open} - {storeHours.close}
          </span>
        </div>
      )}

      {/* Time Selector */}
      {selectedDate && selectedDayData && (
        <div className="time-picker-section">
          <div className="time-picker-header">
            <Clock size={18} />
            <span>Select Time</span>
          </div>

          <div className="time-list">
            {selectedDayData.slots.map((slot, index) => {
              // ✅ Normalize both times for comparison
              const normalizedSlotTime = slot.time?.split(':').slice(0, 2).join(':') || '';
              const normalizedSelectedTime = selectedTime?.split(':').slice(0, 2).join(':') || '';
              const isSelected = normalizedSelectedTime === normalizedSlotTime && normalizedSelectedTime !== '';

              return (
                <button
                  key={`${slot.time}-${index}`}
                  className={`time-slot ${isSelected ? 'selected' : ''} ${slot.disabled ? 'disabled' : ''}`}
                  onClick={() => !slot.disabled && handleTimeSelect(slot.time)}
                  disabled={slot.disabled}
                  title={slot.disabled ? slot.reason : ''}
                  aria-label={`${slot.display}`}
                >
                  {slot.display}
                  {slot.disabled && slot.reason && (
                    <span className="time-slot-reason">{slot.reason}</span>
                  )}
                </button>
              );
            })}
          </div>

          {selectedDayData.slots.length === 0 && (
            <div className="time-picker-empty" style={{ padding: '20px' }}>
              <AlertCircle size={20} />
              <p style={{ fontSize: '14px' }}>No available time slots for this date</p>
            </div>
          )}
        </div>
      )}

      {/* Selected Summary */}
      {selectedDate && selectedDayData && selectedTime && (
        <div className="time-picker-summary">
          <div className="summary-icon">
            <Clock size={20} />
          </div>
          <div className="summary-text">
            <strong>
              {orderType === 'collection' ? 'Pickup Time:' : 'Delivery Time:'}
            </strong>
            <span>
              {selectedDayData.displayDate} at {formatTimeDisplay(selectedTime)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;